import React from "react";
import { X } from "lucide-react";

export default function CategoryPickerPopup({
  isOpen,
  onClose,
  options = [],
  selectedIds = [],
  selectedNames = [],
  onToggle,
  title = "Select Categories",
  variant = "modal",
  header = null,
  dropdownClassName = "",
  searchTerm = "",
  onCreateSuggestion,
}) {
  // Filter options locally by searchTerm — simple contiguous substring match
  const query = (searchTerm || "").trim().toLowerCase();
  const filteredOptions = options.filter((opt) => {
    if (!opt) return false;
    if (!query) return true;
    const name = typeof opt === "object" ? opt.name || opt.title || opt.label || "" : String(opt);
    return name.toLowerCase().includes(query);
  });

  // Only suppress "Create" when query exactly matches an existing tag name (case-insensitive)
  const hasExactMatch = React.useMemo(() => {
    if (!query) return true; // no query → no Create button
    const inOptions = options.some((opt) => {
      const name = typeof opt === "object" ? opt.name || opt.title || opt.label || "" : String(opt);
      return name.toLowerCase() === query;
    });
    if (inOptions) return true;

    return selectedNames.some((name) => {
      return String(name || "").toLowerCase() === query;
    });
  }, [options, selectedNames, query]);

  if (!isOpen) return null;

  const innerContent = (
    <>
      {header}
      {/* Content - Scrollable without scrollbar, Brick Layout */}
      <div 
        className="p-4 overflow-y-auto max-h-[170px]"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {filteredOptions.length === 0 ? (
          searchTerm ? (
            <div className="flex flex-col items-center justify-center gap-3 py-6 text-center px-4">
              <p className="text-sm text-[#9CA3AF]" style={{ fontFamily: "CabinetGrotesk-Regular" }}>
                No results for <span className="font-semibold text-[#FF4C18]">"{searchTerm}"</span>
              </p>
              <button
                type="button"
                onClick={() => onCreateSuggestion && onCreateSuggestion(searchTerm)}
                className="px-6 py-1.5 bg-[#FF4C18] text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                style={{ fontFamily: "CabinetGrotesk-Bold" }}
              >
                Create
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">No options available</p>
          )
        ) : (
          <div className="flex flex-wrap gap-2">
            {filteredOptions.map((opt) => {
              const id = opt._id || opt;
              const name = opt.name || opt;
              const isSelected = selectedIds.some(sid => {
                if (!sid) return false;
                const sidStr = String(sid).toLowerCase();
                return sidStr === String(id).toLowerCase() || sidStr === String(name).toLowerCase();
              });

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onToggle(opt)}
                  className={`px-3.5 py-1.5 text-xs sm:text-sm rounded-xl border transition-all duration-200 whitespace-nowrap ${
                    isSelected
                      ? "bg-[#FF4C18] text-white border-[#FF4C18] font-medium"
                      : "bg-white text-gray-700 border-gray-200 hover:border-[#FF4C18] hover:text-[#FF4C18] hover:bg-orange-50/50"
                  }`}
                  style={{ fontFamily: "CabinetGrotesk-Regular" }}
                >
                  {name}
                </button>
              );
            })}
            {!hasExactMatch && query && (
              <button
                type="button"
                onClick={() => onCreateSuggestion && onCreateSuggestion(searchTerm)}
                className="px-3.5 py-1.5 text-xs sm:text-sm rounded-xl border border-dashed border-[#FF4C18] text-[#FF4C18] font-semibold bg-orange-50/50 hover:bg-orange-50 transition-all duration-200 whitespace-nowrap"
                style={{ fontFamily: "CabinetGrotesk-Bold" }}
              >
                + Create "{searchTerm}"
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (variant === "dropdown") {
    return (
      <div 
        className={`absolute top-full mt-2 z-50 w-[350px] sm:w-[450px] max-w-[calc(100vw-32px)] left-0 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-gray-100 flex flex-col overflow-hidden max-h-[170px] ${dropdownClassName}`}
      >
        {innerContent}
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col relative overflow-hidden"
        style={{ maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {innerContent}
      </div>
    </div>
  );
}
